import { useEffect, useState } from "react";
import { Server, Database, Cpu, HardDrive, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

const fmtUptime = (s) => {
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function InfraTab() {
  const auth = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    auth.apiGet("/admin/health")
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudo conectar al cluster"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [auth]); // eslint-disable-line

  if (!data) return <p className="text-sm text-ink-mid p-8 text-center" data-testid="infra-loading">Conectando con Ovamx Cloud…</p>;

  const ok = data.status === "healthy";

  return (
    <div data-testid="tab-infra" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Ovamx Cloud · {data.region}</p>
          <p className="mt-1 text-[11px] text-ink-mid font-mono">
            Plataforma alojada en infraestructura gestionada por Emergent. DigitalOcean no se utiliza en este entorno.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="infra-refresh"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50"
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
        </button>
      </div>

      <section className={`border rounded-2xl p-5 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="infra-status-card">
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${ok ? "bg-emerald-500 animate-pulse" : "bg-amber-500 animate-pulse"}`} />
          <h3 className="font-serif text-2xl tracking-tight">
            {ok ? "Todos los sistemas operativos" : "Servicio degradado"}
          </h3>
        </div>
        <p className="mt-2 text-xs text-ink-mid font-mono">
          {data.cluster} · nodo {data.node} · v{data.version} · verificado {new Date(data.checked_at).toLocaleTimeString("es")}
        </p>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Cpu}     label="CPU"           value={`${data.cpu_pct}%`} />
        <Stat icon={Activity} label="Memoria"      value={`${data.memory.pct}%`}
              sub={`${data.memory.used_gb}/${data.memory.total_gb} GB`} />
        <Stat icon={HardDrive} label="Disco"       value={`${data.disk.pct}%`}
              sub={`${data.disk.used_gb}/${data.disk.total_gb} GB`} />
        <Stat icon={Server}   label="Uptime app"   value={fmtUptime(data.uptime.app_seconds)}
              sub={`Host: ${fmtUptime(data.uptime.host_seconds)}`} />
      </section>

      <section className="border hairline bg-cream-card rounded-2xl p-5" data-testid="infra-db">
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-coral" />
          <h3 className="font-serif text-xl tracking-tight">{data.database.engine}</h3>
          <span className={`ml-auto text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${data.database.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
            {data.database.ok ? `OK · ${data.database.latency_ms} ms` : "OFFLINE"}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.database.collections).map(([k, v]) => (
            <div key={k} data-testid={`infra-coll-${k}`} className="border hairline rounded-xl px-3 py-2 bg-cream">
              <p className="font-mono text-[10px] uppercase tracking-wider text-ink-mid">{k}</p>
              <p className="font-serif text-xl tracking-tight">{v}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="border hairline bg-cream-card rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-coral" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">{label}</p>
      </div>
      <p className="font-serif text-2xl tracking-tighter">{value}</p>
      {sub && <p className="text-[11px] text-ink-mid mt-1 font-mono">{sub}</p>}
    </div>
  );
}
